import { useEffect, useRef, useCallback } from 'react';
import { Article, ArticleVariant, PaginatedResponse } from '@repo/types';
import { Pagination } from '../../../siteStructure/components/Pagination';
import { ArticleCard, ArticleCardSkeleton } from '../ArticleCard';
import classNames from 'classnames';
import { ArticleListProps, ArticleListVariant } from './contracts';

export type ArticleListUIProps = ArticleListProps & {
  paginatedArticles: PaginatedResponse<Article>;
  allArticles?: Article[];
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  lazyLoad?: boolean;
};

export default function ArticleListUI({
  title,
  paginatedArticles,
  allArticles,
  withPagination,
  lazyLoad = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
  variant,
  listVariant,
  showDate,
  showAuthor,
  showImage,
  showSummary,
  showTag,
  columns = 1,
  prioritize = false,
}: ArticleListUIProps) {
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const articles = lazyLoad && allArticles ? allArticles : paginatedArticles?.items ?? [];

  // Infinite scroll observer
  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];
      if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage && fetchNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  useEffect(() => {
    if (!lazyLoad) return;

    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: '100px',
      threshold: 0,
    });

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [lazyLoad, handleIntersection]);

  if (articles.length === 0 && !isFetchingNextPage) {
    return null;
  }

  const className = classNames('article-card-list-container', {
    'article-card-list-container--with-pagination': withPagination && !lazyLoad,
    'article-card-list-container--lazy-load': lazyLoad,
    'article-card-list-container--default': variant === ArticleVariant.Default,
    'article-card-list-container--text-side-by-side': variant === ArticleVariant.TextSideBySide,
    'article-card-list-container--two-column-grid': listVariant === ArticleListVariant.TwoColumnGrid,
  });

  const skeletonCount = columns * 2;

  return (
    <div className={className} id="article-list">
      {title && <h3 className="article-card-list-container__title">{title}</h3>}
      <ul className="article-card-list-container__list" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
        {articles.map((article) => {
          return (
            <li className="article-card-list-container__list-item" key={article.id}>
              <ArticleCard
                variant={variant}
                article={article}
                showDate={showDate}
                showAuthor={showAuthor}
                showImage={showImage}
                showSummary={showSummary}
                showTag={showTag}
                prioritize={prioritize}
              />
            </li>
          );
        })}
        {lazyLoad &&
          isFetchingNextPage &&
          Array.from({ length: skeletonCount }).map((_, index) => (
            <li
              className="article-card-list-container__list-item article-card-list-container__list-item--skeleton"
              key={`skeleton-${index}`}
            >
              <ArticleCardSkeleton
                variant={variant}
                showImage={showImage}
                showTag={showTag}
                showDate={showDate}
                showAuthor={showAuthor}
                showSummary={showSummary}
              />
            </li>
          ))}
      </ul>
      {lazyLoad && <div ref={loadMoreRef} className="article-card-list-container__load-more-trigger" />}
      {withPagination && !lazyLoad && (
        <Pagination
          forcePage={paginatedArticles.pagination.currentPage - 1}
          pageCount={paginatedArticles.pagination.totalPages}
        />
      )}
    </div>
  );
}
